import { Mail, RefreshCw, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import { formatRelativeTime } from '@/utils/formatters';
import { useSyncAccount, useDeleteAccount } from '@/hooks/useAccounts';

export default function AccountCard({ account, onEdit }) {
  const syncMutation = useSyncAccount();
  const deleteMutation = useDeleteAccount();

  const handleSync = () => {
    syncMutation.mutate(account.id);
  };

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete "${account.name}"?`)) {
      deleteMutation.mutate(account.id);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{account.name}</h3>
                <p className="text-sm text-muted-foreground truncate">{account.email}</p>
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Server:</span>
                <span>{account.imap_host}:{account.imap_port}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Last Sync:</span>
                <span>{formatRelativeTime(account.last_sync)}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Sync Interval:</span>
                <span>{account.sync_interval}s</span>
              </div>

              <div className="flex items-center gap-2 mt-3">
                {account.enabled ? (
                  <Badge variant="success" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    Disabled
                  </Badge>
                )}

                {account.imap_tls && (
                  <Badge variant="outline">TLS</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSync}
              loading={syncMutation.isPending}
              disabled={!account.enabled}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Sync
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(account)}
            >
              <Edit className="h-4 w-4" />
            </Button>

            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
